import type { RiskAssessment } from '@nestjs-boilerplate/types';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertCircle, ArrowUpDown, MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { RiskLevelBadge } from './risk-level-badge';

function formatDate(date: Date | null): string {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date));
}

interface ColumnsProps {
  onViewReport?: (assessment: RiskAssessment) => void;
  onGenerateLink?: (assessment: RiskAssessment) => void;
  onDelete?: (assessment: RiskAssessment) => void;
  onViewDetails?: (assessment: RiskAssessment) => void;
  onRefresh?: (assessment: RiskAssessment) => void;
  onRerunAssessment?: (assessment: RiskAssessment) => void;
  documentUploadEnabled?: boolean;
}

export function getColumns({
  onViewReport,
  onGenerateLink,
  onDelete,
  onViewDetails,
  onRefresh,
  onRerunAssessment,
  documentUploadEnabled = false,
}: ColumnsProps = {}): ColumnDef<RiskAssessment>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          // Purged rows have nothing batch-actionable left. Disable the
          // checkbox so a user can't accidentally include them in a batch
          // refresh / delete.
          disabled={!!row.original.purgedAt}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'optimalKycRef',
      header: 'Optimal KYC Ref',
      cell: ({ row }) => {
        const assessment = row.original;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#051923]">{row.getValue('optimalKycRef')}</span>
            {assessment.purgedAt && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600">
                    Redacted
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Personal data and report content were removed after the retention window. The
                    audit reference, risk outcome, and which checks ran are retained for compliance.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {!assessment.purgedAt && assessment.needsRefresh && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>This report has expired. Refresh to ensure data is up to date.</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'clientRef',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Client Ref
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span className="text-[#051923]">{row.getValue('clientRef')}</span>,
    },
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      cell: ({ row }) => {
        const name = row.getValue('clientName') as string;
        if (!name && row.original.purgedAt) {
          // Redacted rows have no client identity left; show the same em-dash
          // placeholder used elsewhere when a field is intentionally blank.
          return <span className="text-gray-400">—</span>;
        }
        return <span className="text-[#051923]">{name}</span>;
      },
    },
    {
      accessorKey: 'riskLevel',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Client Risk Level
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <RiskLevelBadge level={row.getValue('riskLevel')} />,
    },
    {
      id: 'documentRequest',
      header: () => (
        <div className="flex items-center gap-2">
          <span>Document Request Link Generated</span>
          {!documentUploadEnabled && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-400">
              Coming Soon
            </span>
          )}
        </div>
      ),
      cell: ({ row }: { row: { original: RiskAssessment } }) => {
        if (!documentUploadEnabled) {
          return <span className="text-gray-300">—</span>;
        }

        const assessment = row.original;
        const hasDocumentRequests =
          assessment.documentRequests && assessment.documentRequests.length > 0;

        if (!hasDocumentRequests) {
          return <span className="text-[#051923]">No</span>;
        }

        return (
          <div className="flex items-center gap-4">
            <span className="text-[#051923]">Yes</span>
            <button
              type="button"
              onClick={() => onViewDetails?.(assessment)}
              className="text-[#051923] underline hover:no-underline"
            >
              View Details
            </button>
          </div>
        );
      },
    },
    {
      id: 'reportWindow',
      header: 'Report Download Window (Days)',
      cell: ({ row }) => {
        const assessment = row.original;
        const days = assessment.reportDownloadWindowDays;
        const expired = assessment.reportExpired;

        if (assessment.purgedAt) {
          return (
            <span
              className="text-xs text-gray-500"
              title="Report content was removed after the retention window"
            >
              Redacted
            </span>
          );
        }

        if (expired) {
          // Mirror the dashboard mobile-card behaviour: gate the refresh
          // button on `canRefresh` (computed server-side — accounts for
          // refresh limit, 30-day cap, and purgedAt).
          return (
            <div className="flex items-center gap-2">
              <span className="text-[#051923]">Expired</span>
              {assessment.canRefresh ? (
                <button
                  type="button"
                  onClick={() => onRefresh?.(assessment)}
                  className="text-[#051923] underline hover:no-underline"
                  title={`${assessment.refreshesRemaining} of ${assessment.refreshesUsed + assessment.refreshesRemaining} refreshes left`}
                >
                  Refresh
                </button>
              ) : (
                <span className="text-xs text-gray-500">No refreshes left</span>
              )}
            </div>
          );
        }

        return <span className="text-[#051923]">{days ?? '-'}</span>;
      },
    },
    {
      accessorKey: 'dateReportGenerated',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Date Report Generated
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-[#051923]">{formatDate(row.getValue('dateReportGenerated'))}</span>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const assessment = row.original;

        // Purged rows have no PII, no report data, no client details, no
        // documents. Every menu action is a no-op. Render nothing in the
        // actions slot — the audit trail stub remains visible elsewhere.
        if (assessment.purgedAt) return null;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={assessment.reportExpired}
                onClick={() => !assessment.reportExpired && onViewReport?.(assessment)}
                title={
                  assessment.reportExpired
                    ? 'Report download window has expired. Refresh to re-enable.'
                    : undefined
                }
              >
                View Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRerunAssessment?.(assessment)}>
                Re-run Assessment
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!documentUploadEnabled}
                onClick={() => documentUploadEnabled && onGenerateLink?.(assessment)}
              >
                Generate Secure Document Request Link
                {!documentUploadEnabled && (
                  <span className="ml-2 text-[10px] text-gray-400">(Coming Soon)</span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete?.(assessment)}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}
